import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import {
  createBatchRecordAsync,
  updateBatchRecordAsync,
  deleteBatchRecordAsync
} from '../store/slices/batchRecordsSlice';
import {
  createDeliveryLogAsync,
  updateDeliveryLogAsync,
  deleteDeliveryLogAsync
} from '../store/slices/deliveryLogsSlice';
import {
  createTraceCodeAsync,
  updateTraceCodeAsync,
  deleteTraceCodeAsync
} from '../store/slices/traceCodesSlice';
import {
  fetchFillingBatches,
  createFillingBatchAsync,
  updateFillingBatchAsync,
  deleteFillingBatchAsync
} from '../store/slices/fillingBatchSlice';
import {
  fetchComponentBatches,
  createComponentBatchAsync,
  updateComponentBatchAsync,
  deleteComponentBatchAsync
} from '../store/slices/componentBatchSlice';
import {
  fetchDoughBatches,
  createDoughBatchAsync,
  updateDoughBatchAsync,
  deleteDoughBatchAsync
} from '../store/slices/doughBatchSlice';
import { fetchRawIngredients } from '../store/slices/rawIngredientsSlice';
import { fetchAllBatchRecordIngredients } from '../store/slices/batchRecordIngredientsSlice';
import { BatchRecordList } from '../features/product-tracking/components/BatchRecordList';
import { DeliveryLogList } from '../features/product-tracking/components/DeliveryLogList';
import { TraceCodeList } from '../features/product-tracking/components/TraceCodeList';
import { BakingProductionLogTable } from '../features/product-tracking/components/BakingProductionLogTable';
import { FillingRecordList } from '../features/product-tracking/components/FillingRecordList';
import { ComponentRecordList } from '../features/product-tracking/components/ComponentRecordList';
import { DoughRecordList } from '../features/product-tracking/components/DoughRecordList';
import { FillingBatch } from '../types/fillingBatch';
import { ComponentBatch } from '../types/componentBatch';
import { DoughBatch, DoughIngredientInput } from '../types/doughBatch';
import { FillingIngredient, FillingComponentBatch as FillingComponentBatchLocal } from '../types/fillingRecord';
import { ComponentIngredient } from '../types/componentRecord';
import { showToast } from '../utils/toastHelper';
import { TraceabilityProvider } from '../components/haccp/TraceabilityContext';

interface CollapsibleSectionProps {
  title: string;
  isExpanded: boolean;
  onToggle: () => void;
  children?: React.ReactNode;
  nested?: boolean;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({ title, isExpanded, onToggle, children, nested = false }) => {
  return (
    <div
      style={{
        border: nested ? '1px solid #e0e0e0' : '2px solid #ddd',
        borderRadius: '8px',
        marginBottom: nested ? '16px' : '20px',
        backgroundColor: 'white',
        overflow: 'hidden'
      }}
    >
      <div
        onClick={onToggle}
        style={{
          padding: '16px',
          backgroundColor: nested ? '#fafafa' : '#f5f5f5',
          borderBottom: isExpanded ? '1px solid #ddd' : 'none',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          transition: 'background-color 0.2s'
        }}
        onMouseEnter={e => (e.currentTarget.style.backgroundColor = nested ? '#f0f0f0' : '#eeeeee')}
        onMouseLeave={e => (e.currentTarget.style.backgroundColor = nested ? '#fafafa' : '#f5f5f5')}
      >
        <h3 style={{ margin: 0, fontSize: nested ? '1.1rem' : '1.2rem' }}>{title}</h3>
        <span
          style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: '#666',
            transition: 'transform 0.2s',
            transform: isExpanded ? (nested ? 'rotate(90deg)' : 'rotate(180deg)') : 'rotate(0deg)',
            display: 'inline-block'
          }}
        >
          {nested ? '▸' : '▼'}
        </span>
      </div>
      {isExpanded && (
        <div style={{ padding: '20px' }}>
          {children || (
            <div
              style={{
                textAlign: 'center',
                padding: '40px 20px',
                backgroundColor: '#f9f9f9',
                borderRadius: '4px',
                color: '#999'
              }}
            >
              <p style={{ fontSize: '1rem', margin: 0 }}>Content coming soon.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const ProductTracking: React.FC = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const batchRecords = useAppSelector(state => state.batchRecords.records);
  const fillings = useAppSelector(state => state.recipes.fillings);
  const components = useAppSelector(state => state.recipes.components);
  const deliveryLogs = useAppSelector(state => state.deliveryLogs.logs);
  const traceCodes = useAppSelector(state => state.traceCodes.traceCodes);
  const fillingBatches = useAppSelector(state => state.fillingBatches.items);
  const componentBatches = useAppSelector(state => state.componentBatches.items);
  const doughBatches = useAppSelector(state => state.doughBatches.items);

  // Read query params for auto-expand
  const targetBatchRecordId = searchParams.get('open');
  const targetBatchField = searchParams.get('field');

  // Capture URL params in state so they survive the 100ms param-clearing effect
  const [savedTargetId] = useState(searchParams.get('open'));
  const [savedTargetField] = useState(searchParams.get('field'));
  const [savedIsFlagged] = useState(searchParams.get('flagged') === 'true');
  const targetBakingId = searchParams.get('openBaking');
  const [savedBakingId] = useState(searchParams.get('openBaking'));
  const [savedBakingField] = useState(searchParams.get('openBaking') ? searchParams.get('field') : null);
  const targetDeliveryId = searchParams.get('openDelivery');
  const [savedDeliveryId] = useState(searchParams.get('openDelivery'));
  const [savedDeliveryField] = useState(searchParams.get('openDelivery') ? searchParams.get('field') : null);
  const targetTraceCodeId = searchParams.get('openTraceCode');
  const [savedTraceCodeId] = useState(searchParams.get('openTraceCode'));
  const [savedTraceCodeField] = useState(searchParams.get('openTraceCode') ? searchParams.get('field') : null);

  // Determine initial expanded state based on query params
  const hasTargetInProduct = !!(targetBatchRecordId || targetBakingId || targetDeliveryId || targetTraceCodeId);

  const [expandedSections, setExpandedSections] = useState({
    product: hasTargetInProduct,
    fillings: false,
    components: false,
    dough: false,
    bakingProduction: hasTargetInProduct ? !!targetBakingId : false,
    traceCode: hasTargetInProduct ? !!targetTraceCodeId : false,
    delivery: hasTargetInProduct ? !!targetDeliveryId : false,
    batchRecord: hasTargetInProduct ? !!targetBatchRecordId : false
  });

  // Clear query params after initial processing
  useEffect(() => {
    if (targetBatchRecordId || targetBakingId || targetDeliveryId || targetTraceCodeId) {
      // Clear params after a short delay to allow components to read them
      const timer = setTimeout(() => {
        setSearchParams({}, { replace: true });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [targetBatchRecordId, targetBakingId, targetDeliveryId, targetTraceCodeId, setSearchParams]);

  // Fetch data on mount
  useEffect(() => {
    dispatch(fetchFillingBatches());
    dispatch(fetchComponentBatches());
    dispatch(fetchDoughBatches());
    dispatch(fetchRawIngredients());
    dispatch(fetchAllBatchRecordIngredients());
  }, [dispatch]);

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Filling batch handlers
  const handleAddFillingBatch = (batch: FillingBatch, ingredients: FillingIngredient[], componentBatches: FillingComponentBatchLocal[]) => {
    dispatch(createFillingBatchAsync({ batch, ingredients, componentBatches }));
  };

  const handleUpdateFillingBatch = (batch: FillingBatch) => {
    dispatch(updateFillingBatchAsync(batch));
  };

  const handleDeleteFillingBatch = (batchId: string) => {
    dispatch(deleteFillingBatchAsync(batchId));
  };

  const handleFillingTimesProducedChange = (id: string, timesProduced: number) => {
    const batch = fillingBatches.find(b => b.id === id);
    if (batch) {
      dispatch(updateFillingBatchAsync({
        ...batch,
        timesProduced
      }));
    }
  };

  // Component batch handlers
  const handleAddComponentBatch = (batch: ComponentBatch, ingredients: ComponentIngredient[]) => {
    dispatch(createComponentBatchAsync({ batch, ingredients }));
  };

  const handleUpdateComponentBatch = (batch: ComponentBatch) => {
    dispatch(updateComponentBatchAsync(batch));
  };

  const handleDeleteComponentBatch = async (batchId: string) => {
    try {
      await dispatch(deleteComponentBatchAsync(batchId)).unwrap();
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to delete component batch', 'error');
    }
  };

  const handleComponentTimesProducedChange = (id: string, timesProduced: number) => {
    const batch = componentBatches.find(b => b.id === id);
    if (batch) {
      dispatch(updateComponentBatchAsync({
        ...batch,
        timesProduced
      }));
    }
  };

  // Dough batch handlers
  const handleAddDoughBatch = (batch: DoughBatch, ingredients: DoughIngredientInput[]) => {
    dispatch(createDoughBatchAsync({
      productName: batch.productName,
      dateOfProduction: batch.dateOfProduction,
      preparedBy: batch.preparedBy,
      verifiedBy: batch.verifiedBy,
      ingredients
    }));
  };

  const handleUpdateDoughBatch = (batch: DoughBatch) => {
    dispatch(updateDoughBatchAsync({
      id: batch.id,
      data: {
        productName: batch.productName,
        dateOfProduction: batch.dateOfProduction,
        batchNumber: batch.batchNumber,
        preparedBy: batch.preparedBy,
        verifiedBy: batch.verifiedBy,
        timesProduced: batch.timesProduced
      }
    }));
  };

  const handleTimesProducedChange = (id: string, timesProduced: number) => {
    // Find the batch to update
    const batch = doughBatches.find(b => b.id === id);
    if (batch) {
      dispatch(updateDoughBatchAsync({
        id,
        data: {
          productName: batch.productName,
          dateOfProduction: batch.dateOfProduction,
          batchNumber: batch.batchNumber,
          preparedBy: batch.preparedBy,
          verifiedBy: batch.verifiedBy,
          timesProduced
        }
      }));
    }
  };

  const handleDeleteDoughBatch = (batchId: string) => {
    dispatch(deleteDoughBatchAsync(batchId));
  };

  return (
    <TraceabilityProvider>
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        overflow: 'hidden'
      }}
    >
      {/* Sticky Header Section */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
          zIndex: 10,
          padding: '20px',
          borderBottom: '1px solid #e0e0e0',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <header
            style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <div>
              <h1 style={{ fontSize: '2rem', color: '#333', margin: '0 0 8px 0' }}>Product Tracking</h1>
              <p style={{ color: '#666', fontSize: '1rem', margin: 0 }}>Track and manage finished product inventory</p>
            </div>
          </header>
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div
        data-scroll-container
        style={{
          flex: 1,
          overflow: 'auto',
          padding: '20px',
          backgroundColor: '#f8fafc'
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <CollapsibleSection
            title="Product"
            isExpanded={expandedSections.product}
            onToggle={() => toggleSection('product')}
          >
            <div style={{ padding: '10px 0', backgroundColor: '#f8f9fa' }}>
              <CollapsibleSection
                title="Baking Production Log"
                isExpanded={expandedSections.bakingProduction}
                onToggle={() => toggleSection('bakingProduction')}
                nested={true}
              >
                <BakingProductionLogTable
                  targetBakingId={savedBakingId || undefined}
                  targetField={savedBakingField || undefined}
                  isFlagged={savedIsFlagged}
                />
              </CollapsibleSection>

              <CollapsibleSection
                title="Finished Product Trace Code Log"
                isExpanded={expandedSections.traceCode}
                onToggle={() => toggleSection('traceCode')}
                nested={true}
              >
                <TraceCodeList
                  traceCodes={traceCodes}
                  fillings={fillings}
                  batchRecords={batchRecords}
                  deliveryLogs={deliveryLogs}
                  onAdd={code => dispatch(createTraceCodeAsync(code))}
                  onUpdate={code => dispatch(updateTraceCodeAsync(code))}
                  onDelete={id => dispatch(deleteTraceCodeAsync(id))}
                  targetTraceCodeId={savedTraceCodeId || undefined}
                  targetField={savedTraceCodeField || undefined}
                  isFlagged={savedIsFlagged}
                />
              </CollapsibleSection>

              <CollapsibleSection
                title="Finished Product Delivery Log"
                isExpanded={expandedSections.delivery}
                onToggle={() => toggleSection('delivery')}
                nested={true}
              >
                <DeliveryLogList
                  logs={deliveryLogs}
                  fillings={fillings}
                  batchRecords={batchRecords}
                  onAdd={log => dispatch(createDeliveryLogAsync(log))}
                  onUpdate={log => dispatch(updateDeliveryLogAsync(log))}
                  onDelete={logId => dispatch(deleteDeliveryLogAsync(logId))}
                  targetDeliveryId={savedDeliveryId || undefined}
                  targetField={savedDeliveryField || undefined}
                  isFlagged={savedIsFlagged}
                />
              </CollapsibleSection>

              <CollapsibleSection
                title="Baking/Cooling/Freezing Batch Record"
                isExpanded={expandedSections.batchRecord}
                onToggle={() => toggleSection('batchRecord')}
                nested={true}
              >
                <BatchRecordList
                  records={batchRecords}
                  fillings={fillings}
                  onAdd={record => dispatch(createBatchRecordAsync(record))}
                  onUpdate={record => dispatch(updateBatchRecordAsync(record))}
                  onDelete={recordId => dispatch(deleteBatchRecordAsync(recordId))}
                  targetId={savedTargetId || undefined}
                  targetField={savedTargetField || undefined}
                  isFlagged={savedIsFlagged}
                />
              </CollapsibleSection>
            </div>
          </CollapsibleSection>

          <CollapsibleSection
            title="Fillings"
            isExpanded={expandedSections.fillings}
            onToggle={() => toggleSection('fillings')}
          >
            <FillingRecordList
              records={fillingBatches}
              fillings={fillings}
              onAdd={handleAddFillingBatch}
              onUpdate={handleUpdateFillingBatch}
              onDelete={handleDeleteFillingBatch}
              onTimesProducedChange={handleFillingTimesProducedChange}
            />
          </CollapsibleSection>

          <CollapsibleSection
            title="Components"
            isExpanded={expandedSections.components}
            onToggle={() => toggleSection('components')}
          >
            <ComponentRecordList
              records={componentBatches}
              components={components}
              onAdd={handleAddComponentBatch}
              onUpdate={handleUpdateComponentBatch}
              onDelete={handleDeleteComponentBatch}
              onTimesProducedChange={handleComponentTimesProducedChange}
            />
          </CollapsibleSection>

          <CollapsibleSection
            title="Dough"
            isExpanded={expandedSections.dough}
            onToggle={() => toggleSection('dough')}
          >
            <DoughRecordList
              records={doughBatches}
              onAdd={handleAddDoughBatch}
              onUpdate={handleUpdateDoughBatch}
              onDelete={handleDeleteDoughBatch}
              onTimesProducedChange={handleTimesProducedChange}
            />
          </CollapsibleSection>
        </div>
      </div>
    </div>
    </TraceabilityProvider>
  );
};
